import { div, h2, p, span, button, img, ul, li, fnstate } from '@srfnstack/fntags'
import { fnfetch } from '../fetch.js'
import { showNotification } from '../state.js'

// AI appeals (the taste challenger): artwork you rejected that the market
// model rates in the top tenth, in features you usually rate below the
// market. One appeal per artwork, ever. "Fine, publish it" sends it back into
// the pipeline exactly as an approval would; "No, still no" is final. The
// same two buttons are on the Discord card; either place decides it.

// topShare names a percentile as "top N%" (same as the Discord card).
export const topShare = (pct) => `top ${Math.max(1, Math.ceil(Math.round((1 - Number(pct || 0)) * 1e6) / 1e4))}%`

export const AiAppeals = () => {
  const appeals = fnstate([], a => a.id)

  const load = async () => {
    try {
      const res = await fetch('/api/taste-appeals?status=open')
      const data = res.ok ? await res.json() : []
      appeals(Array.isArray(data) ? data : [])
    } catch {
      appeals([])
    }
  }
  load()

  const appealCard = (aState) => {
    const a = aState()
    const busy = fnstate(false)
    const decide = async (decision) => {
      busy(true)
      const res = await fnfetch(`/api/taste-appeals/${a.id}/${decision}`, { method: 'POST' })
      if (res.ok) {
        appeals(appeals().filter(x => x.id !== a.id))
        showNotification(decision === 'accept' ? 'Back in the pipeline as an approval.' : 'Still no. That is final.', 'success')
      } else {
        const e = await res.json().catch(() => ({}))
        showNotification(`Failed to ${decision}: ${e.error || res.status}`, 'error')
        busy(false)
      }
    }
    return div({ class: 'card bg-base-200 border border-warning/40 p-4 flex flex-col gap-3 sm:flex-row' },
      a.artwork_path
        ? img({
          src: `/api/image?file=${encodeURIComponent(a.artwork_path)}`,
          class: 'w-full sm:w-48 aspect-square object-contain rounded bg-base-100',
          loading: 'lazy',
          decoding: 'async'
        })
        : div({ class: 'w-full sm:w-48 aspect-square rounded bg-base-100 flex items-center justify-center opacity-50' }, 'no image'),
      div({ class: 'flex-1 min-w-0 flex flex-col gap-2' },
        div({ class: 'flex flex-wrap items-center gap-2' },
          span({ class: 'font-semibold text-sm' }, a.title || `Artwork #${a.asset_id}`),
          span({ class: 'badge badge-warning badge-sm' }, `market appeal ${topShare(a.percentile)}`)
        ),
        p({ class: 'text-sm' }, 'I think this one could be a winner and it may just not be your taste.'),
        ul({ class: 'text-xs opacity-80 list-disc pl-4 flex flex-col gap-1' }, (a.evidence || []).map(e => li(e))),
        div({ class: 'flex gap-2 mt-auto' },
          button({ class: 'btn btn-sm btn-success', disabled: busy.bindAttr(b => b), onclick: () => decide('accept') }, 'Fine, publish it'),
          button({ class: 'btn btn-sm btn-ghost', disabled: busy.bindAttr(b => b), onclick: () => decide('decline') }, 'No, still no')
        )
      )
    )
  }

  return div({ class: 'flex flex-col gap-2 max-w-5xl mb-6' },
    appeals.bindAs(list => list.length === 0
      ? span()
      : div({ class: 'flex flex-col gap-1' },
        h2({ class: 'text-lg font-bold' }, 'AI appeals'),
        span({ class: 'text-sm opacity-70' },
          'Artwork you rejected that the market model rates highly, in features you usually rate below the market. One appeal per artwork; you always have the last word.')
      )
    ),
    appeals.bindChildren(div({ class: 'flex flex-col gap-3' }), appealCard)
  )
}
